import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Save, ShieldAlert, Settings as SettingsIcon } from 'lucide-react';

export default function WorkflowSettings() {
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await api.get('/settings');
            const sorted = (response.data || []).sort((a, b) => a.key.localeCompare(b.key));
            setSettings(sorted);
        } catch (error) {
            console.error('Erro ao buscar configurações:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = (key) => {
        setSettings(prev => prev.map(s => {
            if (s.key === key) {
                return {
                    ...s,
                    value: {
                        ...s.value,
                        enabled: !s.value.enabled
                    }
                };
            }
            return s;
        }));
    };

    const handleSave = async (key) => {
        setSaving(true);
        const setting = settings.find(s => s.key === key);
        try {
            await api.put(`/settings/${key}`, { value: setting.value });
            alert('Configuração salva com sucesso!');
        } catch (error) {
            alert('Erro ao salvar: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500">Carregando configurações...</div>;

    return (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-white dark:from-gray-800 dark:to-gray-800 p-6 rounded-xl border border-blue-100 dark:border-gray-700">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
                        <SettingsIcon size={24} />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-gray-800 dark:text-white">Fluxos de Trabalho</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Personalize quais etapas estão ativas no hospital.</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                    {settings.map((setting) => (
                        <div key={setting.key} className="flex items-start justify-between p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 transition-colors shadow-sm">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <h3 className="font-bold text-gray-800 dark:text-gray-200">
                                        {setting.value.label || setting.key}
                                    </h3>
                                    <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${setting.value.enabled ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                                        {setting.value.enabled ? 'Ativo' : 'Inativo'}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                                    {setting.description || 'Sem descrição'}
                                </p>
                            </div>

                            <div className="flex flex-col items-end gap-3">
                                <button
                                    onClick={() => handleToggle(setting.key)}
                                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${setting.value.enabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                                        }`}
                                >
                                    <span
                                        className={`${setting.value.enabled ? 'translate-x-7' : 'translate-x-1'
                                            } inline-block h-6 w-6 transform rounded-full bg-white transition-transform`}
                                    />
                                </button>

                                <button
                                    onClick={() => handleSave(setting.key)}
                                    disabled={saving}
                                    className="text-xs flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-semibold"
                                >
                                    <Save size={14} /> Salvar
                                </button>
                            </div>
                        </div>
                    ))}

                    {settings.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                            Nenhuma configuração disponível.
                        </div>
                    )}
                </div>
            </div>

            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800">
                <h4 className="flex items-center gap-2 font-bold text-yellow-800 dark:text-yellow-500 text-sm mb-2">
                    <ShieldAlert size={16} /> Importante
                </h4>
                <p className="text-xs text-yellow-700 dark:text-yellow-400">
                    Desabilitar um fluxo (ex: Lavanderia) fará com que o sistema pule automaticamente a etapa correspondente.
                    Isso afeta alertas automáticos de Check-in e Check-out.
                </p>
            </div>
        </div>
    );
}
